import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { ticketsApi } from '../api/tickets';
import { productLinesApi } from '../api/productLines';
import { useClassifications } from '../hooks/useClassifications';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import AskSearch from '../components/tickets/AskSearch';
import type { SearchQueryResult } from '../api/ai';
import { StatusBadge, PriorityBadge, TypeBadge } from '../components/badges';
import {
  DraftMark, WaitingOnYouMark, TaskForYouMark, TaskProgressChip, TaskForYouList, taskForYouTitleStyle,
} from '../components/tickets/TicketRowMarks';
import { useTicketStatuses, ACTIVE_CATEGORY_CSV } from '../hooks/useTicketStatuses';
import { groupTicketsByProductLine, hasProductLineGroups, UNASSIGNED_KEY } from '../lib/ticketGrouping';
import type { Ticket, TicketStatus } from '../types';

// Sentinels for the status filter. "Active" = every status whose category is
// not resolved/closed; "cat:<category>" filters by semantic category (used by
// Home stat-box links, where per-company keys aren't known).
const ACTIVE_STATUS = '__active__';
const CAT_PREFIX = 'cat:';

// Product-line filter: the "no product line" sentinel is UNASSIGNED_KEY (shared
// with the grouping helper). Empty selection = show all.
const plStorageKey = (companyId: string) => `ctticket-pl-filter:${companyId}`;

// Rows fetched per slice. The list loads slice by slice as you scroll rather
// than paging, so this is a network chunk size, not a page size. The API caps
// `limit` at 100.
const SLICE_SIZE = 50;

// Cap on slices restored from the URL in one go, so coming Back to a deep
// scroll position can't fire an unbounded burst of requests.
const MAX_RESTORED_SLICES = 10;

function readPlFilter(key: string): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

// Sortable table columns → server sort field, with each column's most useful
// first-click direction (dates newest/soonest first, priority high first,
// text A→Z). Clicking the active column again flips the direction.
const SORT_COLUMNS = [
  { field: 'ticketNumber', label: '#', defaultDir: 'desc' },
  { field: 'title', label: 'Title', defaultDir: 'asc' },
  { field: 'type', label: 'Type', defaultDir: 'asc' },
  { field: 'status', label: 'Status', defaultDir: 'asc' },
  { field: 'priority', label: 'Priority', defaultDir: 'desc' },
  { field: 'assignedTo', label: 'Assigned', defaultDir: 'asc' },
  { field: 'dueDate', label: 'Due', defaultDir: 'asc' },
  { field: 'updatedAt', label: 'Updated', defaultDir: 'desc' },
  { field: 'createdAt', label: 'Created', defaultDir: 'desc' },
] as const;

// Sorts offered as named presets in the dropdown; a header-click sort that
// isn't one of these gets a generated option so the select stays truthful.
const SORT_PRESETS = new Set([
  'createdAt_desc', 'createdAt_asc', 'updatedAt_desc', 'priority_desc', 'dueDate_asc',
]);

function sortLabel(sort: string): string {
  const [field, dirPart] = sort.split('_');
  const col = SORT_COLUMNS.find((c) => c.field === field);
  const name = field === 'ticketNumber' ? 'Ticket #' : col?.label ?? field;
  return `${name} (${dirPart === 'asc' ? 'ascending' : 'descending'})`;
}

/** Compact relative time for the Updated column ("3m", "2h", "5d", else a date). */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '-';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '-';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
}

export default function TicketList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const myId = (user as any)?.id ?? (user as any)?._id;
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial filters can be seeded from the URL (e.g. dashboard stat-box links).
  // Default to active (unresolved) tickets — newest first — per common request.
  // ?status= carries per-company status keys ("all" = the explicit
  // all-statuses choice); ?statusCategory= carries a semantic category
  // (from Home links) and maps to the cat: sentinel.
  const [status, setStatus] = useState<string>(() => {
    const s = searchParams.get('status');
    if (s === 'all') return '';
    if (s) return s;
    const cat = searchParams.get('statusCategory');
    if (cat) return `${CAT_PREFIX}${cat}`;
    return ACTIVE_STATUS;
  });
  const [priority, setPriority] = useState<string>(searchParams.get('priority') ?? '');
  const [type, setType] = useState<string>(searchParams.get('type') ?? '');

  // Filter lists come from the company's classifications. Retired values are
  // kept here (unlike the create form) — tickets filed before a value was
  // turned off still carry it, so you must be able to filter for them.
  const { byKind: classificationsOf } = useClassifications();
  const priorityFilters = classificationsOf('priority');
  const typeFilters = classificationsOf('type');
  const [sort, setSort] = useState<string>(searchParams.get('sort') ?? 'createdAt_desc');
  const [assignedToMe, setAssignedToMe] = useState(
    !!myId && searchParams.get('assignedTo') === myId,
  );
  // "Me"-relative views (Home links carry them): tickets waiting on me, and
  // tickets I have an unsent draft on.
  const [waitingOnMe, setWaitingOnMe] = useState(searchParams.get('waitingOnMe') === '1');
  const [myDrafts, setMyDrafts] = useState(searchParams.get('draft') === 'mine');
  // Tickets with an open sub-ticket task assigned to me ("my work now").
  const [taskForMe, setTaskForMe] = useState(searchParams.get('taskForMe') === '1');
  const urlSearch = searchParams.get('search') ?? '';
  const [search, setSearch] = useState(urlSearch);
  // How many slices should be loaded. Starts at whatever ?page= restored (so
  // Back from a ticket returns you to the same depth) and grows as you scroll.
  const [slices, setSlices] = useState(() => {
    const n = parseInt(searchParams.get('page') ?? '1', 10);
    return Math.min(MAX_RESTORED_SLICES, Math.max(1, Number.isNaN(n) ? 1 : n));
  });
  // "Ask" mode: a question is translated into filters, which then drive this
  // same list. While a question is active it supplies the filters wholesale —
  // mixing it with the manual controls would make neither one readable.
  const [askMode, setAskMode] = useState(false);
  const [askResult, setAskResult] = useState<SearchQueryResult | null>(null);
  const askActive = askMode && !!askResult;

  // Adopt an externally navigated ?search= (TopBar global search). Skip when
  // it already matches — on mount that would clobber the ?page= restored above.
  const prevUrlSearch = useRef(urlSearch);
  useEffect(() => {
    if (prevUrlSearch.current === urlSearch) return;
    prevUrlSearch.current = urlSearch;
    setSearch(urlSearch);
    setSlices(1);
  }, [urlSearch]);

  // Mirror the filters into the URL (replacing the current history entry) so
  // opening a ticket and clicking Back restores this exact view — filters,
  // search, sort, and scroll depth all survive the round-trip. Defaults are
  // omitted to keep the URL clean; "all statuses" needs an explicit sentinel
  // to stay distinguishable from the Active default.
  useEffect(() => {
    const params = new URLSearchParams();
    if (status === '') params.set('status', 'all');
    else if (status.startsWith(CAT_PREFIX)) params.set('statusCategory', status.slice(CAT_PREFIX.length));
    else if (status !== ACTIVE_STATUS) params.set('status', status);
    if (priority) params.set('priority', priority);
    if (type) params.set('type', type);
    if (sort !== 'createdAt_desc') params.set('sort', sort);
    if (assignedToMe && myId) params.set('assignedTo', myId);
    if (waitingOnMe) params.set('waitingOnMe', '1');
    if (myDrafts) params.set('draft', 'mine');
    if (taskForMe) params.set('taskForMe', '1');
    if (search) params.set('search', search);
    if (slices > 1) params.set('page', String(slices));
    setSearchParams(params, { replace: true });
  }, [status, priority, type, sort, assignedToMe, waitingOnMe, myDrafts, taskForMe, myId, search, slices, setSearchParams]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [bulkNote, setBulkNote] = useState('');

  // Product-line filter — persisted per company across sessions.
  const { currentCompany } = useCompany();
  const companyKey =
    (currentCompany as any)?.id ??
    (currentCompany as any)?.uuid ??
    localStorage.getItem('ctticket-company') ??
    'default';
  const storageKey = plStorageKey(companyKey);
  const [selectedPls, setSelectedPls] = useState<string[]>(() => readPlFilter(storageKey));
  useEffect(() => {
    setSelectedPls(readPlFilter(storageKey));
  }, [storageKey]);

  const [plMenuOpen, setPlMenuOpen] = useState(false);
  const plMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!plMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!plMenuRef.current?.contains(e.target as Node)) setPlMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPlMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [plMenuOpen]);

  const { data: productLinesData } = useQuery({
    queryKey: ['product-lines'],
    queryFn: productLinesApi.list,
  });
  const productLines = productLinesData ?? [];

  // Company's custom status set — drives the filter and bulk-change options.
  const { statuses: statusDefs, categoryOf } = useTicketStatuses();
  const activeStatusDefs = statusDefs.filter(s => s.isActive);

  // Translate the select's value into API params (keys vs. category sentinel).
  const statusParams = status === ACTIVE_STATUS
    ? { statusCategory: ACTIVE_CATEGORY_CSV }
    : status.startsWith(CAT_PREFIX)
      ? { statusCategory: status.slice(CAT_PREFIX.length) }
      : status
        ? { status }
        : {};

  // Drop persisted ids whose product line has since been deleted/deactivated.
  const plFilter = useMemo(() => {
    if (!productLinesData) return selectedPls;
    const valid = new Set([UNASSIGNED_KEY, ...productLinesData.map((pl) => pl.id)]);
    return selectedPls.filter((k) => valid.has(k));
  }, [selectedPls, productLinesData]);

  const plFeatureOn = productLines.length > 0;
  const plActive = plFeatureOn && plFilter.length > 0;

  const setPlFilterPersist = (next: string[]) => {
    setSelectedPls(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
    setSlices(1);
  };
  const togglePl = (key: string) => {
    setPlFilterPersist(
      plFilter.includes(key) ? plFilter.filter((k) => k !== key) : [...plFilter, key],
    );
  };

  // One long list loaded slice by slice, rather than discrete pages. Changing
  // any filter changes the key, which resets it back to a single slice.
  const {
    data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'tickets', status, priority, type, search, sort, assignedToMe, waitingOnMe, myDrafts, taskForMe, myId,
      plActive ? plFilter.join(',') : '',
      askActive ? JSON.stringify(askResult!.filters) : '',
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      askActive
        ? ticketsApi.list({ ...askResult!.filters, sort, page: pageParam, limit: SLICE_SIZE })
        : ticketsApi.list({
            ...statusParams,
            ...(priority && { priority }),
            ...(type && { type }),
            ...(search && { search }),
            ...(assignedToMe && myId && { assignedTo: myId }),
            ...(waitingOnMe && { waitingOnMe: '1' as const }),
            ...(myDrafts && { draft: 'mine' as const }),
            ...(taskForMe && { taskForMe: '1' as const }),
            ...(plActive && { productLineId: plFilter.join(',') }),
            sort,
            page: pageParam,
            limit: SLICE_SIZE,
          }),
    getNextPageParam: (last) => (last.page < (last.pages ?? 1) ? last.page + 1 : undefined),
  });

  const loadedSlices = data?.pages.length ?? 0;
  const tickets = useMemo(
    () => (data?.pages.flatMap((p) => p.tickets) ?? []) as Ticket[],
    [data],
  );
  // `total` is the match count, which every slice reports identically.
  const total = data?.pages[0]?.total ?? 0;
  const allLoaded = !hasNextPage;

  // Ask for one more slice. Guarded so a sentinel that sits in view (a list
  // shorter than the viewport) can't queue up slices faster than they load.
  const loadMore = () => {
    if (!hasNextPage || isFetchingNextPage) return;
    setSlices(loadedSlices + 1);
  };

  // Single mechanism for both growing the list and restoring scroll depth on
  // Back: pull slices until as many are loaded as are wanted.
  useEffect(() => {
    if (loadedSlices > 0 && loadedSlices < slices && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [loadedSlices, slices, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Auto-load as the bottom of the list comes into view, so it reads as one
  // continuously scrolling page. The Load more button below is the same action
  // for keyboards and for browsers without IntersectionObserver.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || allLoaded || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [allLoaded, loadedSlices]);

  const ticketId = (t: Ticket) => (t as any).id ?? (t as any)._id;

  // Group every loaded ticket by product line. Grouping over the whole loaded
  // list — not one slice — is what keeps each product line to a single section
  // with Unassigned last, instead of repeating both per slice.
  const groups = useMemo(
    () => groupTicketsByProductLine(tickets, productLines),
    [tickets, productLines],
  );

  // Hide section headers when nothing loaded has a product line.
  const showGroupHeaders = hasProductLineGroups(groups);

  const bulkMutation = useMutation({
    mutationFn: async ({ ids, newStatus, note }: { ids: string[]; newStatus: TicketStatus; note?: string }) => {
      const cat = categoryOf(newStatus);
      const extra = note
        ? cat === 'resolved' ? { resolution: note }
        : cat === 'closed' ? { closedReason: note }
        : undefined
        : undefined;
      await Promise.all(ids.map((id) => ticketsApi.updateStatus(id, newStatus, extra)));
    },
    onSuccess: (_, { ids }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-stats'] });
      queryClient.invalidateQueries({ queryKey: ['home-my-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['home-aged-tickets'] });
      setSelected(new Set());
      setBulkStatus('');
      setBulkNote('');
      toast.success(`Updated ${ids.length} ticket(s)`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkAssignMutation = useMutation({
    mutationFn: async ({ ids, assignedToId }: { ids: string[]; assignedToId: string | null }) => {
      await Promise.all(ids.map((id) => ticketsApi.update(id, { assignedToId })));
    },
    onSuccess: (_, { ids, assignedToId }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setSelected(new Set());
      toast.success(`${assignedToId ? 'Assigned' : 'Unassigned'} ${ids.length} ticket(s)`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isSlaBreached = (t: Ticket) =>
    !!((t as any).slaResponseBreached || (t as any).slaResolutionBreached);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === tickets.length) setSelected(new Set());
    else setSelected(new Set(tickets.map(ticketId)));
  };

  // Resolving/closing in bulk requires the same note the detail page asks
  // for; the backend rejects a resolve without a resolution and a close of an
  // unresolved ticket without a reason.
  const bulkCategory = bulkStatus ? categoryOf(bulkStatus) : undefined;
  const bulkNeedsNote = bulkCategory === 'resolved' || bulkCategory === 'closed';

  const handleBulkStatus = () => {
    if (!bulkStatus || selected.size === 0) return;
    if (bulkNeedsNote && !bulkNote.trim()) return;
    bulkMutation.mutate({
      ids: Array.from(selected),
      newStatus: bulkStatus as TicketStatus,
      note: bulkNote.trim() || undefined,
    });
  };

  // Don't navigate to the ticket when clicking interactive cells (checkbox).
  const openTicket = (t: Ticket) => navigate(`/tickets/${ticketId(t)}`);

  // Column-header sorting — first click uses the column's natural direction,
  // clicking the active column again flips it.
  const [sortField, sortDir] = sort.split('_');
  const onHeaderSort = (col: (typeof SORT_COLUMNS)[number]) => {
    const nextDir = col.field === sortField
      ? (sortDir === 'asc' ? 'desc' : 'asc')
      : col.defaultDir;
    setSort(`${col.field}_${nextDir}`);
    setSlices(1);
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Tickets"
        intro="View, filter and open support tickets. Click a row to read the thread and reply."
        actions={
          <Link to="/tickets/new" className="btn btn-primary">
            New ticket
          </Link>
        }
      />

      <div className="card space-y-4">
        {/* Search / Ask toggle */}
        <div className="flex items-center gap-1 text-[12px] font-semibold">
          {([['search', 'Search'], ['ask', 'Ask']] as const).map(([mode, label]) => {
            const on = (mode === 'ask') === askMode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => { setAskMode(mode === 'ask'); setSlices(1); }}
                className={`px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                  on ? 'bg-primary-muted border-primary text-primary' : 'border-line text-muted hover:text-fg'
                }`}
              >
                {mode === 'ask' && <Sparkles className="w-3 h-3" />} {label}
              </button>
            );
          })}
          <span className="text-[11px] font-normal text-muted ml-1">
            {askMode
              ? 'Ask in plain English — it becomes filters, and you can see which.'
              : 'Matches ticket text, the conversation and the submitter.'}
          </span>
        </div>

        {askMode && (
          <AskSearch
            result={askResult}
            onResult={(r) => {
              setAskResult(r);
              // Adopt an AI-chosen sort into the shared sort state so the
              // header arrow reflects it and a header click can override it.
              if (r?.filters.sort) setSort(r.filters.sort);
              setSlices(1);
            }}
            total={total}
            loading={isLoading}
          />
        )}

        <div className={`flex flex-wrap gap-3 ${askActive ? 'opacity-40 pointer-events-none' : ''}`}>
          <input
            type="search"
            placeholder="Search tickets, replies, notes, people…"
            title="Searches ticket text, the conversation (replies and internal notes) and the submitter — use it to find how a past issue was resolved"
            className="input max-w-xs"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSlices(1);
            }}
          />
          <select
            className="select w-40"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setSlices(1);
            }}
          >
            <option value={ACTIVE_STATUS}>Active (open)</option>
            <option value="">All statuses</option>
            {/* Category filter arriving via a Home link — surface it so the
                select doesn't show a wrong value. */}
            {status.startsWith(CAT_PREFIX) && (
              <option value={status}>
                {status.slice(CAT_PREFIX.length).replace(/_/g, ' ')} (category)
              </option>
            )}
            {activeStatusDefs.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select
            className="select w-40"
            value={priority}
            onChange={(e) => {
              setPriority(e.target.value);
              setSlices(1);
            }}
          >
            <option value="">All priorities</option>
            {priorityFilters.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <select
            className="select w-40"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setSlices(1);
            }}
          >
            <option value="">All types</option>
            {typeFilters.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          {plFeatureOn && (
            <div ref={plMenuRef} className="relative">
              <button
                type="button"
                className={`btn ${plActive ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPlMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={plMenuOpen}
              >
                {plActive ? `Product lines (${plFilter.length})` : 'All product lines'}
                <ChevronDown className="w-3 h-3" />
              </button>
              {plMenuOpen && (
                <div className="scpa-menu left-0 top-full mt-1.5 max-h-72 overflow-y-auto" role="menu">
                  <button
                    type="button"
                    className="scpa-menu-item"
                    onClick={() => {
                      setPlFilterPersist([]);
                      setPlMenuOpen(false);
                    }}
                  >
                    Show all
                  </button>
                  {productLines.map((pl) => (
                    <label key={pl.id} className="scpa-menu-item cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={plFilter.includes(pl.id)}
                        onChange={() => togglePl(pl.id)}
                      />
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: pl.color }}
                      />
                      <span className="truncate">{pl.name}</span>
                    </label>
                  ))}
                  <label className="scpa-menu-item cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={plFilter.includes(UNASSIGNED_KEY)}
                      onChange={() => togglePl(UNASSIGNED_KEY)}
                    />
                    <span className="truncate">Unassigned</span>
                  </label>
                </div>
              )}
            </div>
          )}
          <select
            className="select w-44"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setSlices(1); }}
          >
            <option value="createdAt_desc">Newest first</option>
            <option value="createdAt_asc">Oldest first</option>
            <option value="updatedAt_desc">Recently updated</option>
            <option value="priority_desc">Priority (high→low)</option>
            <option value="dueDate_asc">Due date (soonest)</option>
            {/* Header-click sorts outside the presets — surface them so the
                select doesn't show a wrong value. */}
            {!SORT_PRESETS.has(sort) && <option value={sort}>{sortLabel(sort)}</option>}
          </select>
          {myId && (
            <button
              type="button"
              className={`btn ${assignedToMe ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setAssignedToMe((v) => !v); setSlices(1); }}
            >
              Assigned to me
            </button>
          )}
          <button
            type="button"
            className={`btn ${waitingOnMe ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setWaitingOnMe((v) => !v); setSlices(1); }}
            title="Tickets in a waiting status where the team needs a reply, test, or confirmation from you"
          >
            Waiting on me
          </button>
          <button
            type="button"
            className={`btn ${myDrafts ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setMyDrafts((v) => !v); setSlices(1); }}
            title="Tickets where you have an unsent reply or note"
          >
            My drafts
          </button>
          <button
            type="button"
            className={`btn ${taskForMe ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setTaskForMe((v) => !v); setSlices(1); }}
            title="Tickets with an open sub-ticket task assigned to you"
          >
            Tasks for me
          </button>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-primary-muted border border-primary">
            <span className="text-sm font-medium text-fg">{selected.size} selected</span>
            <select
              className="select w-36"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
            >
              <option value="">Change status to...</option>
              {activeStatusDefs.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            {bulkNeedsNote && (
              <input
                type="text"
                className="input w-72"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder={bulkCategory === 'resolved'
                  ? 'Resolution (required) — applied to all selected'
                  : 'Reason for closing (required) — applied to all selected'}
              />
            )}
            <button
              className="btn btn-primary"
              onClick={handleBulkStatus}
              disabled={!bulkStatus || (bulkNeedsNote && !bulkNote.trim()) || bulkMutation.isPending}
            >
              Apply
            </button>
            {myId && (
              <button
                className="btn btn-secondary"
                onClick={() => bulkAssignMutation.mutate({ ids: Array.from(selected), assignedToId: myId })}
                disabled={bulkAssignMutation.isPending}
              >
                Assign to me
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => bulkAssignMutation.mutate({ ids: Array.from(selected), assignedToId: null })}
              disabled={bulkAssignMutation.isPending}
            >
              Unassign
            </button>
            <button className="btn btn-ghost" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {isLoading ? (
          <div className="py-12 text-center text-muted">Loading tickets...</div>
        ) : error ? (
          <div className="py-12 text-center text-err">
            Failed to load tickets: {(error as Error).message}
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState>
            No tickets found. Use <b>New ticket</b> to create one.
          </EmptyState>
        ) : (
          <>
            {/* Mobile: stacked ticket cards — the 9-column table needs ~700px
                and is unreadable at phone width */}
            <div className="sm:hidden">
              {groups.map((group) => (
                <Fragment key={group.key}>
                  {showGroupHeaders && (
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-muted"
                      style={{ background: 'var(--scpa-bg)' }}
                    >
                      {group.color && (
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ background: group.color }}
                        />
                      )}
                      {group.name}
                      <span className="font-medium">({group.tickets.length})</span>
                    </div>
                  )}
                  {group.tickets.map((ticket) => (
                    <div
                      key={ticketId(ticket)}
                      className="flex gap-3 p-3 border-b border-line-light active:bg-row-odd"
                      onClick={() => openTicket(ticket)}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(ticketId(ticket))}
                        onChange={() => toggleSelect(ticketId(ticket))}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded mt-1 shrink-0"
                      />
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono font-bold text-primary text-sm">
                            {(ticket as any).ticketNumber ?? ticket.ticketNumber}
                          </span>
                          {isSlaBreached(ticket) && (
                            <span title="SLA breached" className="text-err text-xs font-bold">
                              SLA
                            </span>
                          )}
                          <StatusBadge status={(ticket as any).status ?? ticket.status} />
                          <PriorityBadge priority={(ticket as any).priority ?? ticket.priority} />
                          {(ticket as any).waitingOnYou && <WaitingOnYouMark />}
                          <DraftMark mine={(ticket as any).hasMyDraft} others={(ticket as any).draftsBy} />
                          <TaskForYouMark tasks={(ticket as any).tasksForYou} />
                          <TaskProgressChip summary={(ticket as any).taskSummary} />
                        </div>
                        <p
                          className={`text-sm ${(ticket as any).isUnread || ((ticket as any).tasksForYou?.length ?? 0) > 0 ? 'font-bold' : 'font-medium'} ${taskForYouTitleStyle((ticket as any).tasksForYou) ? '' : 'text-fg'}`}
                          style={taskForYouTitleStyle((ticket as any).tasksForYou)}
                        >
                          {(ticket as any).isUnread && (
                            <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1.5 align-middle" />
                          )}
                          {ticket.title}
                          {taskForMe && <TaskForYouList tasks={(ticket as any).tasksForYou} />}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                          <TypeBadge type={(ticket as any).type ?? ticket.type ?? 'incident'} />
                          <span>{(ticket as any).assignedTo?.name ?? 'Unassigned'}</span>
                          {(ticket as any).dueDate && (
                            <span>Due {new Date((ticket as any).dueDate).toLocaleDateString()}</span>
                          )}
                          <span>Updated {timeAgo((ticket as any).lastActivityAt ?? (ticket as any).updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </Fragment>
              ))}
            </div>
          <table className="scpa-table hidden sm:table">
            <thead>
              <tr>
                <th className="scpa-th" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selected.size === tickets.length && tickets.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                {SORT_COLUMNS.map((col) => {
                  const active = col.field === sortField;
                  return (
                    <th
                      key={col.field}
                      className="scpa-th"
                      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      <button
                        type="button"
                        onClick={() => onHeaderSort(col)}
                        title={`Sort by ${col.field === 'ticketNumber' ? 'ticket number' : col.label.toLowerCase()}`}
                        className={`inline-flex items-center gap-1 font-bold hover:text-fg transition-colors ${
                          active ? 'text-fg' : ''
                        }`}
                      >
                        {col.label}
                        {active && (sortDir === 'asc'
                          ? <ChevronUp className="w-3 h-3" />
                          : <ChevronDown className="w-3 h-3" />)}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.key}>
                  {showGroupHeaders && (
                    <tr>
                      <td
                        colSpan={10}
                        className="scpa-td"
                        style={{
                          background: 'var(--scpa-bg)',
                          fontWeight: 700,
                          fontSize: 12,
                          color: 'var(--scpa-text-muted)',
                        }}
                      >
                        <span className="inline-flex items-center gap-2">
                          {group.color && (
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full"
                              style={{ background: group.color }}
                            />
                          )}
                          {group.name}
                          <span style={{ fontWeight: 500 }}>({group.tickets.length})</span>
                        </span>
                      </td>
                    </tr>
                  )}
                  {group.tickets.map((ticket) => (
                <tr
                  key={ticketId(ticket)}
                  className="scpa-row"
                  onClick={() => openTicket(ticket)}
                >
                  <td className="scpa-td" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(ticketId(ticket))}
                      onChange={() => toggleSelect(ticketId(ticket))}
                      className="rounded"
                    />
                  </td>
                  <td className="scpa-td">
                    <div className="flex items-center gap-2">
                      {(ticket as any).isUnread && (
                        <span
                          title="Updated since you last read it"
                          className="inline-block w-2 h-2 rounded-full bg-primary shrink-0"
                        />
                      )}
                      <span className="font-mono font-bold text-primary">
                        {(ticket as any).ticketNumber ?? ticket.ticketNumber}
                      </span>
                      {isSlaBreached(ticket) && (
                        <span title="SLA breached" className="text-err text-xs font-bold">
                          SLA
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="scpa-td">
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span
                        className={
                          ((ticket as any).tasksForYou?.length ?? 0) > 0 || (ticket as any).isUnread
                            ? 'font-bold text-fg' : 'font-medium text-fg'
                        }
                        style={taskForYouTitleStyle((ticket as any).tasksForYou)}
                      >
                        {ticket.title}
                      </span>
                      {(ticket as any).waitingOnYou && <WaitingOnYouMark />}
                      <DraftMark mine={(ticket as any).hasMyDraft} others={(ticket as any).draftsBy} />
                      <TaskForYouMark tasks={(ticket as any).tasksForYou} />
                      <TaskProgressChip summary={(ticket as any).taskSummary} />
                    </span>
                    {/* In the Tasks-for-me view the task names are the point — list them. */}
                    {taskForMe && <TaskForYouList tasks={(ticket as any).tasksForYou} />}
                  </td>
                  <td className="scpa-td">
                    <TypeBadge type={(ticket as any).type ?? ticket.type ?? 'incident'} />
                  </td>
                  <td className="scpa-td">
                    <StatusBadge status={(ticket as any).status ?? ticket.status} />
                  </td>
                  <td className="scpa-td">
                    <PriorityBadge priority={(ticket as any).priority ?? ticket.priority} />
                  </td>
                  <td className="scpa-td text-muted">
                    {(ticket as any).assignedTo?.name ?? '-'}
                  </td>
                  <td className="scpa-td text-muted">
                    {(ticket as any).dueDate ? new Date((ticket as any).dueDate).toLocaleDateString() : '-'}
                  </td>
                  <td className={`scpa-td ${(ticket as any).isUnread ? 'font-semibold text-fg' : 'text-muted'}`}>
                    {timeAgo((ticket as any).lastActivityAt ?? (ticket as any).updatedAt)}
                  </td>
                  <td className="scpa-td text-muted">
                    {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : '-'}
                  </td>
                </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Scroll sentinel — crossing it pulls the next slice in. */}
      <div ref={sentinelRef} aria-hidden="true" />

      {!isLoading && !error && tickets.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted" role="status" aria-live="polite">
            {allLoaded
              ? `Showing all ${total} ticket${total === 1 ? '' : 's'}`
              : `Showing ${tickets.length} of ${total}`}
          </p>
          {!allLoaded && (
            <button
              className="btn btn-secondary"
              disabled={isFetchingNextPage}
              onClick={loadMore}
            >
              {isFetchingNextPage ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
